import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Button from '@material-ui/core/Button';
import { useHistory } from 'react-router';
import InputAdornment from '@material-ui/core/InputAdornment';
import EuroIcon from '@mui/icons-material/Euro';
import { CreditCard } from '@material-ui/icons';
import helexiaIconDate from '../../images/helexia_icon_date.svg';
import helexiaIconCard from '../../images/helexia_icon_card.svg';
import helexiaIconMoney from '../../images/helexia_icon_money.svg';
import { FormContainer, StyledTextField } from './FormStyles';
import i18n from '../../i18n/i18n';
import { useTheme } from '../../contexts/ThemeContext';
import { searchSession } from '../../api/api';
import NotificationToast from '../notifications/NotificationToast';

const StyledDiv = styled.div`
    padding-top: 20px;
    padding-bottom: 20px;
`;

const HelexiaInputWrapper = styled.div<{ $hasError?: boolean }>`
  background: white;
  border-radius: 12px;
  height: 36px;
  padding: 0 10px;
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  box-sizing: border-box;
  border: 2px solid ${({ $hasError }) => ($hasError ? '#d01818' : 'transparent')};
`;

const HelexiaInput = styled.input`
  background: transparent;
  border: none;
  outline: none;
  color: #3f3f3f;
  font-family: Geologica, sans-serif;
  font-weight: 300;
  font-size: 14px;
  letter-spacing: -0.98px;
  width: 100%;
  &::placeholder {
    color: #6a6a6a;
  }
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0px 1000px white inset;
    box-shadow: 0 0 0px 1000px white inset;
    -webkit-text-fill-color: #3f3f3f;
  }
  &::-webkit-calendar-picker-indicator {
    display: none;
    -webkit-appearance: none;
  }
  &::-webkit-date-and-time-value {
    text-align: left;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  flex:1;
  margin-top: 5dvh;
`;

// Galp date input: stretch the browser's native calendar picker indicator to
// cover the whole field (invisible) so clicking anywhere in the input opens the
// native date picker directly (no showPicker()/user-gesture quirks). The visible
// Figma calendar icon is rendered separately on top (non-interactive).
const GalpDateInput = styled.input`
  position: relative;
  &::-webkit-calendar-picker-indicator {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
    cursor: pointer;
    opacity: 0;
  }
`;

const StyledButton = styled(({ ...rest }) => (
  <Button
    {...rest}
  />
))`
  && {
    background-color: ${(props) => props.color};
    color: ${(props) => props.textColor};
    border-width: 0;
    border-radius: 10px;
    padding: 10px 20px;
    margin: 10px;
    width: 120px;
    text-transform: capitalize;
    font-size: 0.85em;

    ${({ disabled }) => disabled && `
     background-color: rgb(243, 243, 243);
  `
}
`;

type Error = {
    date: string | null,
    digits: string | null,
    amount: string | null,
    vendingMachineId?: string | null,
}

export default function SearchDocumentForm({
  vendingMachineId, setLoading, userDetails, isHelexia = false, isGalp = false, onValidityChange,
}: any) {
  const [date, setDate] = useState('');
  const [digits, setDigits] = useState('');
  const [amount, setAmount] = useState('');
  const [hasError, setHasError] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const dateInputRef = useRef<HTMLInputElement>(null);
  const [dateFocused, setDateFocused] = useState(false);
  const [notification, setNotification] = useState<any>(null);
  const [showNotification, setShowNotification] = useState(false);

  const [error, setError] = useState<Error>({
    date: null,
    digits: null,
    amount: null,
    vendingMachineId: null,
  });

  const { theme } = useTheme();
  const history = useHistory();

  const handleError = useCallback((newError) => {
    setHasError(newError);
  }, []);

  useEffect(() => {
    const newErrors = {
      ...error,
    };

    if (!date) {
      newErrors.date = i18n.t('mandatory');
      handleError(true);
    } else {
      newErrors.date = null;
    }

    if (!digits) {
      newErrors.digits = i18n.t('mandatory');
      handleError(true);
    } else if (digits.length !== 4) {
      newErrors.digits = i18n.t('digits_length');
      handleError(true);
    } else {
      newErrors.digits = null;
    }

    if (!amount) {
      newErrors.amount = i18n.t('mandatory');
      handleError(true);
    } else {
      newErrors.amount = null;
    }

    if (date && digits && amount) {
      handleError(false);
    }

    setError(newErrors);
  }, [date, digits, amount, handleError]);

  useEffect(() => {
    if (onValidityChange) onValidityChange(hasError);
  }, [hasError, onValidityChange]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (hasError) return;
    setLoading(true);
    try {
      const response = await searchSession({
        date,
        digits,
        amount: String(Math.round(parseFloat(amount) * 100)),
        vendingMachineId,
        clientTimezone: Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone,
      });
      history.push(`/session/documents/${response.data.id}`);
      handleError(false);
    } catch (ex) {
      // @ts-ignore
      if (ex.body.message === 'MULTIPLE_SESSIONS_FOUND') {
        setNotification({
          severity: 'error',
          title: i18n.t('error'),
          message: i18n.t('please_contact_merchant'),
        });
        setShowNotification(true);
      } else {
        setNotification({
          severity: 'error',
          title: i18n.t('error'),
          message: i18n.t('error_occured_try_later'),
        });
        setShowNotification(true);
      }
    }
    setLoading(false);
  };

  if (isGalp) {
    const galpFieldGroup: React.CSSProperties = {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
      width: '100%',
    };
    const galpLabel: React.CSSProperties = {
      fontSize: '14px',
      lineHeight: '22px',
      color: '#111110',
      margin: 0,
    };
    const galpInputBox: React.CSSProperties = {
      border: '2px solid #bab9ba',
      borderRadius: '14px',
      height: '46px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '0 10px',
      boxSizing: 'border-box',
      width: '100%',
    };
    const galpInput: React.CSSProperties = {
      border: 'none',
      outline: 'none',
      background: 'transparent',
      width: '100%',
      fontFamily: theme.bodyFont,
      fontSize: '16px',
      color: '#0b0b0b',
    };
    const galpErrorStyle: React.CSSProperties = { color: '#d01818', fontSize: '12px', margin: '4px 0 0 0' };

    return (
      <>
        <NotificationToast
          open={showNotification}
          message={notification && notification.message}
          title={notification && notification.title}
          severity={notification && notification.severity}
          onClose={() => setShowNotification(false)}
        />
        <form
          id="search"
          noValidate
          autoComplete="off"
          onSubmit={onSubmit}
          style={{
            display: 'flex', flexDirection: 'column', gap: '20px', width: '100%',
          }}
        >
          <div style={galpFieldGroup}>
            <label htmlFor="galp-search-date" style={galpLabel}>{i18n.t('date_formatted')}</label>
            <div style={{ ...galpInputBox, padding: 0, position: 'relative' }}>
              {!date && (
                <span style={{
                  position: 'absolute',
                  left: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  color: '#616061',
                  fontFamily: theme.bodyFont,
                  fontSize: '14px',
                  whiteSpace: 'nowrap',
                }}
                >
                  DD/MM/YYYY
                </span>
              )}
              <GalpDateInput
                id="galp-search-date"
                ref={dateInputRef}
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                onFocus={() => setDateFocused(true)}
                onBlur={() => setDateFocused(false)}
                style={{
                  ...galpInput,
                  height: '100%',
                  padding: '0 34px 0 10px',
                  colorScheme: 'light',
                  color: date ? '#0b0b0b' : 'transparent',
                }}
              />
              <img
                src="/companies/GALP/date_icon.svg"
                alt=""
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '18px',
                  pointerEvents: 'none',
                }}
              />
            </div>
            {submitted && error.date && <p style={galpErrorStyle}>{error.date}</p>}
          </div>

          <div style={galpFieldGroup}>
            <label htmlFor="galp-search-digits" style={galpLabel}>{i18n.t('card_last_digits')}</label>
            <div style={galpInputBox}>
              <input
                id="galp-search-digits"
                type="number"
                value={digits}
                onChange={(e) => setDigits(e.target.value)}
                style={galpInput}
              />
              <img
                src="/companies/GALP/card_field_icon.svg"
                alt=""
                style={{ width: '20px', flexShrink: 0 }}
              />
            </div>
            {submitted && error.digits && <p style={galpErrorStyle}>{error.digits}</p>}
          </div>

          <div style={galpFieldGroup}>
            <label htmlFor="galp-search-amount" style={galpLabel}>{i18n.t('amount')}</label>
            <div style={galpInputBox}>
              <input
                id="galp-search-amount"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                style={galpInput}
              />
              <img
                src="/companies/GALP/euro_icon.svg"
                alt=""
                style={{ width: '20px', flexShrink: 0 }}
              />
            </div>
            {submitted && error.amount && <p style={galpErrorStyle}>{error.amount}</p>}
          </div>
        </form>
      </>
    );
  }

  if (isHelexia) {
    const helexiaErrorStyle: React.CSSProperties = {
      color: '#d01818',
      fontFamily: 'Geologica, sans-serif',
      fontSize: '12px',
      margin: '4px 0 0 0',
    };

    return (
      <>
        <NotificationToast
          open={showNotification}
          message={notification && notification.message}
          title={notification && notification.title}
          severity={notification && notification.severity}
          onClose={() => setShowNotification(false)}
        />
        <form id="search" noValidate autoComplete="off" onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%' }}>
          <div>
            <HelexiaInputWrapper $hasError={submitted && !!error.date}>
              <img
                src={helexiaIconDate}
                alt=""
                style={{ width: '22px', flexShrink: 0, cursor: 'pointer' }}
                onClick={() => {
                  try {
                    (dateInputRef.current as any).showPicker();
                  } catch {
                    if (dateInputRef.current) dateInputRef.current.focus();
                  }
                }}
              />
              <div style={{ position: 'relative', flex: 1, display: 'flex', alignItems: 'center' }}>
                {!date && !dateFocused && (
                  <span style={{
                    position: 'absolute',
                    left: 0,
                    pointerEvents: 'none',
                    color: '#6a6a6a',
                    fontFamily: 'Geologica, sans-serif',
                    fontWeight: 300,
                    fontSize: '14px',
                    letterSpacing: '-0.98px',
                    whiteSpace: 'nowrap',
                  }}
                  >
                    {`${i18n.t('date_formatted') as string} *`}
                  </span>
                )}
                <HelexiaInput
                  ref={dateInputRef}
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  onFocus={() => setDateFocused(true)}
                  onBlur={() => setDateFocused(false)}
                  style={{ colorScheme: 'light', color: date || dateFocused ? '#3f3f3f' : 'transparent' }}
                />
              </div>
            </HelexiaInputWrapper>
            {submitted && error.date && <p style={helexiaErrorStyle}>{error.date}</p>}
          </div>
          <div>
            <HelexiaInputWrapper $hasError={submitted && !!error.digits}>
              <img src={helexiaIconCard} alt="" style={{ width: '22px', flexShrink: 0 }} />
              <HelexiaInput
                type="number"
                placeholder={`${i18n.t('card_last_digits') as string} *`}
                value={digits}
                onChange={(e) => setDigits(e.target.value)}
              />
            </HelexiaInputWrapper>
            {submitted && error.digits && <p style={helexiaErrorStyle}>{error.digits}</p>}
          </div>
          <div>
            <HelexiaInputWrapper $hasError={submitted && !!error.amount}>
              <img src={helexiaIconMoney} alt="" style={{ width: '22px', flexShrink: 0 }} />
              <HelexiaInput
                type="number"
                placeholder={`${i18n.t('amount') as string} *`}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </HelexiaInputWrapper>
            {submitted && error.amount && <p style={helexiaErrorStyle}>{error.amount}</p>}
          </div>
        </form>
      </>
    );
  }

  return (
    <FormContainer>
      <NotificationToast
        open={showNotification}
        message={notification && notification.message}
        title={notification && notification.title}
        severity={notification && notification.severity}
        onClose={() => setShowNotification(false)}
      />
      <form id="search" noValidate autoComplete="off" onSubmit={onSubmit}>
        <StyledDiv>
          <span>{i18n.t('date_formatted')}</span>
          <StyledTextField
            fullWidth
            type="date"
            onChange={(e) => setDate(e.target.value)}
            id="date"
            error={!!error?.date}
            helperText={error?.date}
          />
        </StyledDiv>
        <StyledDiv>
          <span>{i18n.t('card_last_digits')}</span>
          <StyledTextField
            fullWidth
            type="number"
            onChange={(e) => setDigits(e.target.value)}
            id="digits"
            error={!!error?.digits}
            helperText={error?.digits}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <CreditCard fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </StyledDiv>
        <StyledDiv>
          <span>{i18n.t('amount')}</span>
          <StyledTextField
            fullWidth
            type="number"
            onChange={(e) => setAmount(e.target.value)}
            id="amount"
            error={!!error?.amount}
            helperText={error?.amount}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <EuroIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </StyledDiv>
        <ButtonsContainer>
          <StyledButton
            variant="outlined"
            color={vendingMachineId ? theme.buttonColor : theme.noVmViewButtonColor || '#f0b239'}
            textColor={vendingMachineId ? theme.buttonTextColor : 'white'}
            type="submit"
            form="search"
            disabled={hasError}
          >
            {i18n.t('search')}
          </StyledButton>
        </ButtonsContainer>
      </form>
    </FormContainer>
  );
}
